import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Alert,
    FormControl,
    Select,
    MenuItem,
    Stack,
    Divider,
    SelectChangeEvent
} from '@mui/material';
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, User } from 'firebase/auth';
import { auth } from '../firebase';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { getUserProfile, updateUserProfile } from '../services/userService';

const Account: React.FC = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState<User | null>(null);
    const [email, setEmail] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState(''); // For re-auth
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const [language, setLanguage] = useState(i18n.language || 'en');

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (u) => {
            if (u) {
                setUser(u);
                setEmail(u.email || '');
                setNewEmail(u.email || '');
                try {
                    const profile = await getUserProfile(u.uid);
                    if (profile && profile.language) {
                        setLanguage(profile.language);
                    }
                } catch (error) {
                    console.error("Error fetching profile", error);
                }
            }
        });
        return () => unsubscribe();
    }, []);

    const handleUpdateEmail = async () => {
        if (!user || !newEmail) return;
        setLoading(true);
        setMessage(null);

        try {
            await updateEmail(user, newEmail);
            setMessage({ type: 'success', text: t('account.email_success') });
            setEmail(newEmail);
        } catch (error: any) {
            console.error('Error updating email:', error);
            if (error.code === 'auth/requires-recent-login') {
                setMessage({ type: 'error', text: t('account.email_error_recent_login') });
            } else {
                setMessage({ type: 'error', text: t('account.email_error_general', { message: error.message }) });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (!user || !password) return;
        if (password !== confirmPassword) {
            setMessage({ type: 'error', text: t('account.password_mismatch') });
            return;
        }
        setLoading(true);
        setMessage(null);

        try {
            await updatePassword(user, password);
            setMessage({ type: 'success', text: t('account.password_success') });
            setPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            console.error('Error updating password:', error);
            if (error.code === 'auth/requires-recent-login') {
                setMessage({ type: 'error', text: t('account.password_error_recent_login') });
            } else {
                setMessage({ type: 'error', text: t('account.password_error_general', { message: error.message }) });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (e: SelectChangeEvent<string>) => {
        const newLang = e.target.value;
        setLanguage(newLang);
        // We do NOT need to call i18n.changeLanguage(newLang) here anymore,
        // because App.tsx listens to the profile doc and updates i18n globally.

        if (user) {
            try {
                // Update local storage and firestore
                await updateUserProfile(user.uid, { language: newLang });
                setMessage({ type: 'success', text: t('settings.save_success') });
                setTimeout(() => setMessage(null), 3000);
            } catch (error) {
                console.error('Error updating language:', error);
                setMessage({ type: 'error', text: t('settings.save_failed') });
            }
        }
    };

    return (
        <Box sx={{ py: 4, minHeight: '80vh' }}>
            <Container maxWidth="sm">
                <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 4, fontWeight: 'bold' }}>
                    {t('account.title')}
                </Typography>

                {message && (
                    <Alert severity={message.type} sx={{ mb: 3 }}>
                        {message.text}
                    </Alert>
                )}

                <Stack spacing={4}>
                    <Card elevation={2} sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                {t('settings.preferred_language')}
                            </Typography>
                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <Select
                                    labelId="language-select-label"
                                    id="language-select"
                                    value={language}
                                    onChange={handleLanguageChange}
                                >
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="de">Deutsch</MenuItem>
                                    <MenuItem value="fr">Français</MenuItem>
                                </Select>
                            </FormControl>
                        </CardContent>
                    </Card>

                    <Card elevation={2} sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                {t('account.update_email_title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('account.update_email_desc')}
                            </Typography>
                            <Box component="form" noValidate autoComplete="off">
                                <TextField
                                    fullWidth
                                    label={t('account.new_email_label')}
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    disabled={loading}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpdateEmail}
                                    disabled={loading || newEmail === email || !newEmail}
                                    sx={{ mt: 2 }}
                                >
                                    {t('account.update_email_btn')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card elevation={2} sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                {t('account.change_password_title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('account.change_password_desc')}
                            </Typography>
                            <Box component="form" noValidate autoComplete="off">
                                <TextField
                                    fullWidth
                                    label={t('account.new_password_label')}
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    disabled={loading}
                                />
                                <TextField
                                    fullWidth
                                    label={t('account.confirm_password_label')}
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    disabled={loading}
                                    error={password !== confirmPassword && confirmPassword !== ''}
                                    helperText={password !== confirmPassword && confirmPassword !== '' ? t('account.password_mismatch') : ""}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpdatePassword}
                                    disabled={loading || !password || !confirmPassword}
                                    sx={{ mt: 2 }}
                                >
                                    {t('account.update_password_btn')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Stack>
            </Container>
        </Box>
    );
};

export default Account;
